import { useEffect, useState } from 'react';
import { Download, X, CheckCircle, AlertCircle, WifiOff } from 'lucide-react';
import { useEnginePack } from '../hooks/useEnginePack';

/**
 * Top-level host for the contextual "download offline engines" prompt.
 *
 * Listens for the `scrivar:offline-engines-needed` event that nativebridge
 * dispatches when a heavy op (compress/OCR/unlock/flatten) is attempted while
 * offline / not-signed-in with no engines installed. Offers the one-time
 * download (reusing useEnginePack). Inert on web / non-desktop builds.
 *
 * No internal tool names surfaced — just "offline engines".
 */
export default function OfflineEnginesPromptHost() {
  const { status, installing, progress, error, install, supported } = useEnginePack();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!supported) return undefined;
    const onNeeded = () => {
      // Skip if engines are already installed (stale event / race).
      if (!status?.installed) setOpen(true);
    };
    window.addEventListener('scrivar:offline-engines-needed', onNeeded);
    return () => window.removeEventListener('scrivar:offline-engines-needed', onNeeded);
  }, [supported, status?.installed]);

  if (!supported || !open) return null;

  const installed = status?.installed;
  const pct = progress?.phase === 'download' ? (progress.percent || 0) : (installing ? 100 : 0);
  const phaseLabel = progress?.phase === 'download'
    ? `Downloading… ${progress.percent || 0}%`
    : progress?.phase === 'extract' ? 'Installing…'
    : installing ? 'Preparing…' : '';

  const close = () => { if (!installing) setOpen(false); };
  // install() (useEnginePack) clears the nativebridge deps cache on success, so
  // the next heavy op sees the freshly-extracted engines without a restart.
  const onDownload = () => install();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="offline-engines-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      onClick={close}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
        {installed ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-500" aria-hidden="true" />
              <h3 id="offline-engines-title" className="text-base font-semibold text-gray-900">
                Offline mode is ready
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              You can now run this tool without an internet connection. Try your action again.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-3 mb-1">
              <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                <WifiOff className="w-5 h-5 text-indigo-600" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h3 id="offline-engines-title" className="text-base font-semibold text-gray-900">
                  Work offline?
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  This tool needs the offline engines to run without an internet connection.
                </p>
                <p className="text-xs text-gray-400 mt-1.5">
                  Open-source tools, downloaded from Scrivar and verified before they run. Your files never leave your computer.
                </p>
              </div>
              {!installing && (
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="ml-auto -mt-1 -mr-1 p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
            </div>

            {installing ? (
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>{phaseLabel}</span>
                  {progress?.phase === 'download' && <span>{progress.percent || 0}%</span>}
                </div>
                <div className="h-2 rounded bg-gray-100 overflow-hidden">
                  <div className="h-2 bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
                <p className="mt-2 text-xs text-gray-400">
                  One-time download (about 120&nbsp;MB). You can keep working while it finishes.
                </p>
              </div>
            ) : (
              <>
                {error && (
                  <div role="alert" className="mt-3 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{error}</span>
                  </div>
                )}
                <div className="mt-5 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Not now
                  </button>
                  <button
                    type="button"
                    onClick={onDownload}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700"
                  >
                    <Download className="w-4 h-4" aria-hidden="true" />
                    Download offline engines
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-gray-400 text-right">About 120&nbsp;MB · one-time</p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
